import { deepEqualIgnoringKeysDeep } from '@fmpedia/helpers'
import { mapGetters } from 'vuex'

/**
 * Please, note that changing this value will affect:
 * 1) Feed pages' "See more" is in "loading" state until this timeout
 * runs out
 */
const DELAYED_REFRESH_TIMEOUT = 5 * 1000

/**
 * This mixin provides a method to delay and refresh content by
 * comparing current data with newly fetched data. It is designed
 * to work in environments where CDN cache is applied,
 * ensuring that users do not receive outdated content.
 */
export default {
  data() {
    return {
      delayedContentRefreshTimeoutId: null,
      isPendingContentRefresh: true
    }
  },
  computed: {
    ...mapGetters({
      routerIsFirstLoad: 'routerIsFirstLoad'
    })
  },
  methods: {
    /**
     * Delays a refresh of content by comparing the current data with newly requested data.
     *
     * This function waits for a specified timeout before executing a data refresh.
     * It compares the "old" data (retrieved by getOldDataFn) with "new" data (returned
     * by requestNewDataFn), ignoring keys specified in dataComparisonKeysToOmitDeep.
     * If a difference is found, the dataChangedCallbackFn is executed with the new data.
     *
     * Note: The content refresh is performed only on the initial (server-side) page load.
     * If the navigation is client-side, the refresh will be skipped.
     *
     * @param {Object} params - The parameters object.
     * @param {Function} params.getOldDataFn - A function that returns the current data.
     * @param {Function} params.requestNewDataFn - An asynchronous function that returns
     *   the new data. This function should return a Promise that resolves to an object
     *   containing the updated data.
     * @param {string[]} [params.dataComparisonKeysToOmitDeep=[]] - An array of key names
     *   that should be ignored when comparing old and new data.
     * @param {Function} [params.dataChangedCallbackFn=() => {}] - A callback function that
     *   will be executed if the comparison indicates that the data has changed. The new
     *   data is passed as an argument to this callback.
     * @param {Function} [params.cancelRefreshConditionFn=() => false] - A function that
     *   will check if the content refresh should be cancelled
     */
    $_delayedContentRefresh_run({
      getOldDataFn,
      requestNewDataFn,
      dataComparisonKeysToOmitDeep = [],
      dataChangedCallbackFn = () => {},
      cancelRefreshConditionFn = () => false
    }) {
      if (!getOldDataFn || !requestNewDataFn) {
        console.error('Please, provide both getOldDataFn and requestNewDataFn')
        this.isPendingContentRefresh = false
        return
      }

      /**
       * Content must be refreshed for the first (server-side) load only
       */
      if (!this.routerIsFirstLoad || cancelRefreshConditionFn()) {
        this.isPendingContentRefresh = false
        return
      }

      this.delayedContentRefreshTimeoutId = setTimeout(async () => {
        try {
          if (cancelRefreshConditionFn()) {
            return
          }

          const newData = await requestNewDataFn()
          const oldData = getOldDataFn()

          /**
           * It is technically possible that cancel condition is changed after
           * DELAYED_REFRESH_TIMEOUT, during we make an actual request to get
           * the newData, so we need to check it again just to be safe
           */
          if (cancelRefreshConditionFn()) {
            return
          }

          const wasContentChanged = !deepEqualIgnoringKeysDeep({
            obj1: oldData,
            obj2: newData,
            keysToOmitDeep: dataComparisonKeysToOmitDeep
          })

          if (wasContentChanged) {
            /**
             * Here is where you would typically call a method
             * to set the updated data and rerender lazy hydrated
             * components if required
             */
            dataChangedCallbackFn(newData)
          }
        } catch (err) {
          this.$errorHandler(err, this)
        } finally {
          this.isPendingContentRefresh = false
        }
      }, DELAYED_REFRESH_TIMEOUT)
    }
  },
  beforeDestroy() {
    if (this.delayedContentRefreshTimeoutId) {
      clearTimeout(this.delayedContentRefreshTimeoutId)
    }
  }
}
